import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  AppsOutlined,
  CalendarMonthOutlined,
  CheckCircleOutlined,
  FilterAltOutlined,
  PaymentsOutlined,
  RefreshOutlined,
  SearchOutlined,
  TableRowsOutlined,
  TrendingUpOutlined,
  CloseOutlined,
  SchoolOutlined,
  ReceiptLongOutlined,
  ArrowForwardOutlined,
} from "@mui/icons-material";

import "./formsales.css";

const API = process.env.REACT_APP_SERVER_SCRIPT_HOST;

const MONTHS = [
  { value: "Jan", label: "January" },
  { value: "Feb", label: "February" },
  { value: "Mar", label: "March" },
  { value: "Apr", label: "April" },
  { value: "May", label: "May" },
  { value: "Jun", label: "June" },
  { value: "Jul", label: "July" },
  { value: "Aug", label: "August" },
  { value: "Sep", label: "September" },
  { value: "Oct", label: "October" },
  { value: "Nov", label: "November" },
  { value: "Dec", label: "December" },
];

const getApplicantName = (item) =>
  [item?.first_Name, item?.sur_Name, item?.other_Name]
    .filter(Boolean)
    .join(" ") || "Unnamed Applicant";

const getInitials = (item) => {
  const name = getApplicantName(item);

  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0))
    .join("")
    .toUpperCase();
};

const getProgramme = (item) =>
  item?.course_of_study ||
  item?.course_of_study ||
  "Programme not specified";

const getReference = (item) =>
  item?.account_status?.reference_number || "Not assigned";

const getDate = (item) =>
  item?.registered_date?.date ||
  item?.registered_date ||
  item?.createdAt ||
  "—";

const getPaymentStatus = (item) =>
  item?.account_status?.paymentStatus || "pending";

const formatDate = (value) => {
  if (!value || value === "—") return "—";

  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) return value;

  return parsed.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

function ApplicantAvatar({ applicant, large = false }) {
  return (
    <div
      className={`sales-avatar ${
        large ? "sales-avatar--large" : ""
      }`}
    >
      {applicant?.passport?.url ? (
        <img
          src={applicant.passport.url}
          alt={getApplicantName(applicant)}
        />
      ) : (
        <span>{getInitials(applicant)}</span>
      )}
    </div>
  );
}

function PaymentStatus({ status }) {
  const normalized = String(status || "pending")
    .toLowerCase()
    .replace(/\s+/g, "-");

  const label =
    normalized === "success"
      ? "Paid"
      : normalized === "successful"
      ? "Paid"
      : normalized === "paid"
      ? "Paid"
      : normalized === "failed"
      ? "Failed"
      : "Pending";

  return (
    <span
      className={`sales-payment-status sales-payment-status--${normalized}`}
    >
      <span />
      {label}
    </span>
  );
}

function MetricCard({
  icon,
  value,
  label,
  description,
  variant = "blue",
}) {
  return (
    <article className={`sales-metric sales-metric--${variant}`}>
      <div className="sales-metric__top">
        <div className="sales-metric__icon">{icon}</div>

        <span className="sales-metric__indicator">
          <span />
          LIVE
        </span>
      </div>

      <div className="sales-metric__value">{value ?? 0}</div>

      <div className="sales-metric__label">{label}</div>

      <p>{description}</p>
    </article>
  );
}

function TableView({ applicants }) {
  return (
    <section className="sales-table-card">
      <div className="sales-table-card__header">
        <div>
          <span className="sales-section-eyebrow">
            APPLICATION SALES
          </span>

          <h2>Application records</h2>

          <p>
            Monitor application sales, payment references and
            registration activity.
          </p>
        </div>

        <div className="sales-record-count">
          <strong>{applicants.length}</strong>
          <span>records</span>
        </div>
      </div>

      <div className="sales-table-scroll">
        <table className="sales-table">
          <thead>
            <tr>
              <th className="sales-sn">#</th>
              <th>Applicant</th>
              <th>Application</th>
              <th>Gender</th>
              <th>Programme</th>
              <th>Payment RRR</th>
              <th>Payment</th>
              <th>Date</th>
            </tr>
          </thead>

          <tbody>
            {applicants.map((applicant, index) => (
              <tr
                key={
                  applicant?._id ||
                  applicant?.application_number ||
                  index
                }
              >
                <td className="sales-sn">
                  {String(index + 1).padStart(2, "0")}
                </td>

                <td>
                  <div className="sales-applicant">
                    <ApplicantAvatar applicant={applicant} />

                    <div>
                      <strong>{getApplicantName(applicant)}</strong>
                      <span>
                        {applicant?.email ||
                          applicant?.phone ||
                          "Applicant profile"}
                      </span>
                    </div>
                  </div>
                </td>

                <td>
                  <span className="sales-application-id">
                    {applicant?.application_number || "N/A"}
                  </span>
                </td>

                <td>
                  <span className="sales-gender">
                    {applicant?.gender || "—"}
                  </span>
                </td>

                <td>
                  <div className="sales-programme">
                    <SchoolOutlined />

                    <div>
                      <strong>{getProgramme(applicant)}</strong>
                      <span>Application programme</span>
                    </div>
                  </div>
                </td>

                <td>
                  <span className="sales-reference">
                    <ReceiptLongOutlined />
                    {getReference(applicant)}
                  </span>
                </td>

                <td>
                  <PaymentStatus
                    status={getPaymentStatus(applicant)}
                  />
                </td>

                <td>
                  <div className="sales-date">
                    <CalendarMonthOutlined />
                    <span>{formatDate(getDate(applicant))}</span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function CardView({ applicants }) {
  return (
    <section className="sales-grid">
      {applicants.map((applicant, index) => (
        <article
          className="sales-applicant-card"
          key={
            applicant?._id ||
            applicant?.application_number ||
            index
          }
        >
          <div className="sales-card__top">
            <div className="sales-card__identity">
              <ApplicantAvatar
                applicant={applicant}
                large
              />

              <div>
                <h3>{getApplicantName(applicant)}</h3>

                <span>
                  {applicant?.application_number ||
                    "No application number"}
                </span>
              </div>
            </div>

            <PaymentStatus
              status={getPaymentStatus(applicant)}
            />
          </div>

          <div className="sales-card__programme">
            <div className="sales-card__programme-icon">
              <SchoolOutlined />
            </div>

            <div>
              <span>PROGRAMME</span>
              <strong>{getProgramme(applicant)}</strong>
            </div>
          </div>

          <div className="sales-card__details">
            <div>
              <span>Gender</span>
              <strong>{applicant?.gender || "—"}</strong>
            </div>

            <div>
              <span>Payment RRR</span>
              <strong>{getReference(applicant)}</strong>
            </div>

            <div>
              <span>Submitted</span>
              <strong>{formatDate(getDate(applicant))}</strong>
            </div>
          </div>

          <div className="sales-card__footer">
            <span>
              <CheckCircleOutlined />
              Application recorded
            </span>

            <ArrowForwardOutlined />
          </div>
        </article>
      ))}
    </section>
  );
}

function EmptyState({ hasFilters, clearFilters }) {
  return (
    <section className="sales-empty">
      <div className="sales-empty__icon">
        <PaymentsOutlined />
      </div>

      <h3>
        {hasFilters
          ? "No matching applications"
          : "No application sales"}
      </h3>

      <p>
        {hasFilters
          ? "Try changing your filters or clearing the current selection."
          : "Application sales and payment records will appear here."}
      </p>

      {hasFilters && (
        <button
          type="button"
          onClick={clearFilters}
        >
          <CloseOutlined />
          Clear filters
        </button>
      )}
    </section>
  );
}

export default function FormSales() {
  const [loading, setLoading] = useState(false);

  const [applicants, setApplicants] = useState([]);

  const [counts, setCounts] = useState({
    count: 0,
    completeds: 0,
  });

  const [changeview, setChangeView] = useState("table");

  const [month, setMonth] = useState("");
  const [course, setCourse] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const getApplicants = async () => {
    try {
      setLoading(true);

      const response = await axios.get(
        `${API}/form_sales`
      );

      const result = response?.data || {};

      setApplicants(result?.response || []);

      setCounts({
        count: result?.count || 0,
        completeds: result?.completeds || 0,
      });
    } catch (error) {
      console.error(
        "Unable to load application sales:",
        error
      );
    } finally {
      setLoading(false);
    }
  };

  const getByFilters = async () => {
    try {
      setLoading(true);

      const response = await axios.post(
        `${API}/formSales/fitlerSearch`,
        {
          month,
          course,
        }
      );

      const result = response?.data || {};

      setApplicants(result?.response || []);

      setCounts({
        count: result?.count || 0,
        completeds: result?.completeds || 0,
      });
    } catch (error) {
      console.error(
        "Unable to filter application sales:",
        error
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getApplicants();
  }, []);

  const visibleApplicants = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();

    if (!query) return applicants;

    return applicants.filter((applicant) => {
      const searchable = [
        getApplicantName(applicant),
        applicant?.application_number,
        applicant?.gender,
        applicant?.course_of_study,
        applicant?.course_of_study,
        applicant?.account_status?.reference_number,
        applicant?.account_status?.paymentStatus,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return searchable.includes(query);
    });
  }, [applicants, searchTerm]);

  const totalAmount = (counts?.count || 0) * 10000;

  const clearFilters = () => {
    setMonth("");
    setCourse("");
    setSearchTerm("");
    getApplicants();
  };

  const hasFilters =
    Boolean(month) ||
    Boolean(course) ||
    Boolean(searchTerm);

  return (
    <main className="forms-sale">
      <div className="forms-sale__container">

        {/* PAGE HEADER */}
        <header className="sales-page-header">
          <div className="sales-page-heading">
            <div className="sales-eyebrow">
              <span />
              APPLICATION SALES
            </div>

            <h1>Form Sales</h1>

            <p>
              Track application purchases, payment references
              and candidate registration activity from one
              workspace.
            </p>
          </div>

          <button
            type="button"
            className="sales-refresh"
            onClick={getApplicants}
            disabled={loading}
          >
            <RefreshOutlined
              className={
                loading
                  ? "sales-refresh--spinning"
                  : ""
              }
            />
            Refresh
          </button>
        </header>

        {/* KPI AREA */}
        <section className="sales-metrics">
          <MetricCard
            icon={<PaymentsOutlined />}
            value={counts?.count}
            label="Total candidates"
            description="Application records received"
            variant="blue"
          />

          <MetricCard
            icon={<TrendingUpOutlined />}
            value={`₦${totalAmount.toLocaleString()}`}
            label="Total amount"
            description="Estimated application revenue"
            variant="yellow"
          />

          <MetricCard
            icon={<CheckCircleOutlined />}
            value={counts?.completeds}
            label="Completed"
            description="Completed application transactions"
            variant="green"
          />

          <MetricCard
            icon={<SchoolOutlined />}
            value={visibleApplicants.length}
            label="Visible records"
            description="Records currently shown"
            variant="neutral"
          />
        </section>

        {/* FILTER WORKSPACE */}
        <section className="sales-filter-card">
          <div className="sales-filter-heading">
            <div className="sales-filter-title">
              <div className="sales-filter-icon">
                <FilterAltOutlined />
              </div>

              <div>
                <span>DATA FILTERS</span>
                <h2>Refine applications</h2>
              </div>
            </div>

            {hasFilters && (
              <button
                type="button"
                className="sales-clear-filter"
                onClick={clearFilters}
              >
                <CloseOutlined />
                Clear all
              </button>
            )}
          </div>

          <div className="sales-filter-grid">
            <label className="sales-field">
              <span>Programme</span>

              <div className="sales-select-wrap">
                <SchoolOutlined />

                <select
                  value={course}
                  onChange={(event) =>
                    setCourse(event.target.value)
                  }
                >
                  <option value="">
                    All programmes
                  </option>

                  <option value="Midwifery">
                    Midwifery
                  </option>

                  <option value="General Nursing">
                    Nursing
                  </option>
                </select>
              </div>
            </label>

            <label className="sales-field">
              <span>Application month</span>

              <div className="sales-select-wrap">
                <CalendarMonthOutlined />

                <select
                  value={month}
                  onChange={(event) =>
                    setMonth(event.target.value)
                  }
                >
                  <option value="">
                    All months
                  </option>

                  {MONTHS.map((item) => (
                    <option
                      key={item.value}
                      value={item.value}
                    >
                      {item.label}
                    </option>
                  ))}
                </select>
              </div>
            </label>

            <div className="sales-filter-action">
              <button
                type="button"
                onClick={getByFilters}
                disabled={loading}
              >
                <FilterAltOutlined />
                Apply filters
              </button>
            </div>
          </div>
        </section>

        {/* DATA TOOLBAR */}
        <section className="sales-toolbar">
          <div className="sales-search">
            <SearchOutlined />

            <input
              type="search"
              value={searchTerm}
              placeholder="Search applicant, application number, programme or RRR..."
              onChange={(event) =>
                setSearchTerm(event.target.value)
              }
            />

            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm("")}
              >
                <CloseOutlined />
              </button>
            )}
          </div>

          <div className="sales-toolbar__right">
            <div className="sales-view-switcher">
              <button
                type="button"
                className={
                  changeview === "table"
                    ? "active"
                    : ""
                }
                onClick={() =>
                  setChangeView("table")
                }
                title="Table view"
              >
                <TableRowsOutlined />
                <span>Table</span>
              </button>

              <button
                type="button"
                className={
                  changeview === "grid"
                    ? "active"
                    : ""
                }
                onClick={() =>
                  setChangeView("grid")
                }
                title="Grid view"
              >
                <AppsOutlined />
                <span>Grid</span>
              </button>
            </div>
          </div>
        </section>

        {/* CONTENT HEADER */}
        <div className="sales-content-heading">
          <div>
            <span>APPLICATION RECORDS</span>

            <strong>
              {visibleApplicants.length}{" "}
              {visibleApplicants.length === 1
                ? "candidate"
                : "candidates"}
            </strong>
          </div>

          {hasFilters && (
            <div className="sales-active-filter">
              <FilterAltOutlined />

              <span>Filters active</span>
            </div>
          )}
        </div>

        {/* CONTENT */}
        {visibleApplicants.length === 0 ? (
          <EmptyState
            hasFilters={hasFilters}
            clearFilters={clearFilters}
          />
        ) : changeview === "grid" ? (
          <CardView applicants={visibleApplicants} />
        ) : (
          <TableView applicants={visibleApplicants} />
        )}

        {/* LOADING OVERLAY */}
        {loading && (
          <div className="sales-loading">
            <div className="sales-loading__spinner" />
            <div>
              <strong>Updating records</strong>
              <span>
                Fetching the latest application data...
              </span>
            </div>
          </div>
        )}
      </div>
    </main>
  );
}